import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import SectionHead from "./SectionHead";
import { img } from "./utils";

const rand = (a, b) => a + Math.random() * (b - a);

const TABS = [
  ["all", "All"],
  ["event", "Store events"],
  ["social", "Social media"],
];

// an uploaded video plays inside its tile, everything else shows the picture
const Media = ({ it }) => {
  if (it.media_type === "video" && it.video) {
    return (
      <video controls preload="none" playsInline src={img(it.video)} poster={it.image ? img(it.image) : undefined} />
    );
  }
  return <img src={img(it.image)} alt={it.caption || "Bapat Optics"} loading="lazy" />;
};

const Caption = ({ it }) =>
  it.caption || it.description ? (
    <figcaption>
      {it.caption && <strong>{it.caption}</strong>}
      {it.description && <span>{it.description}</span>}
    </figcaption>
  ) : null;

const Lightbox = ({ items, index, onClose, onNav }) => {
  const it = items[index];
  const closeRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onNav]);

  useEffect(() => {
    closeRef.current?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (!it) return null;
  return (
    <motion.div
      className="au-lb"
      role="dialog"
      aria-modal="true"
      aria-label={it.caption || "Gallery image"}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button type="button" ref={closeRef} className="au-lb__btn au-lb__close" onClick={onClose} aria-label="Close">
        ✕
      </button>
      {items.length > 1 && (
        <>
          <button
            type="button"
            className="au-lb__btn au-lb__nav au-lb__nav--prev"
            onClick={(e) => {
              e.stopPropagation();
              onNav(-1);
            }}
            aria-label="Previous image"
          >
            ‹
          </button>
          <button
            type="button"
            className="au-lb__btn au-lb__nav au-lb__nav--next"
            onClick={(e) => {
              e.stopPropagation();
              onNav(1);
            }}
            aria-label="Next image"
          >
            ›
          </button>
        </>
      )}
      <AnimatePresence mode="wait">
        <motion.figure
          key={it.id}
          className="au-lb__fig"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src={img(it.image)} alt={it.caption || "Bapat Optics"} />
          <Caption it={it} />
        </motion.figure>
      </AnimatePresence>
    </motion.div>
  );
};

const Gallery = ({ items = [] }) => {
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(-1);
  const opener = useRef(null);

  
  const scatter = useMemo(
    () =>
      Object.fromEntries(items.map((it) => [it.id, { x: rand(-320, 320), y: rand(-120, 280), r: rand(-14, 14) }])),
    [items]
  );

  const hasEvent = items.some((i) => i.category === "event");
  const hasSocial = items.some((i) => i.category === "social");
  const tabs = TABS.filter(([k]) => k === "all" || (k === "event" ? hasEvent : hasSocial));
  const shown = tab === "all" ? items : items.filter((i) => i.category === tab);

  // tiles that open in the lightbox: pictures without an outside link
  const isZoom = (it) => !(it.media_type === "video" && it.video) && !it.link && it.image;
  const zoomable = shown.filter(isZoom);

  const close = useCallback(() => {
    setOpen(-1);
    opener.current?.focus?.();
  }, []);
  const nav = useCallback(
    (d) => setOpen((o) => (zoomable.length ? (o + d + zoomable.length) % zoomable.length : -1)),
    [zoomable.length]
  );

  if (!items.length) return null;

  return (
    <section id="gallery" className="au-sec au-sec--night">
      <div className="au-wrap">
        <SectionHead
          eyebrow="Moments"
          title="Gallery"
          lede="Moments from our store events and the best of what we share on social media."
        />

        {tabs.length > 2 && (
          <div className="au-gal__tabs" role="tablist" aria-label="Gallery filter">
            {tabs.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={tab === k}
                className={tab === k ? "is-on" : ""}
                onClick={() => setTab(k)}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <div className="au-gal">
          <AnimatePresence mode="popLayout">
            {shown.map((it, i) => {
              const s = scatter[it.id] || { x: 0, y: 60, r: 0 };
              const inlineVideo = it.media_type === "video" && it.video;
              const zi = zoomable.findIndex((z) => z.id === it.id);
              return (
                <motion.figure
                  layout
                  key={it.id}
                  className={`au-gal__item g${i % 8}`}
                  initial={{ opacity: 0, scale: 0.7, x: s.x, y: s.y, rotate: s.r }}
                  whileInView={{ opacity: 1, scale: 1, x: 0, y: 0, rotate: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ type: "spring", stiffness: 70, damping: 16, delay: (i % 8) * 0.06 }}
                >
                  {inlineVideo ? (
                    <Media it={it} />
                  ) : it.link ? (
                    <a
                      className="au-gal__link"
                      href={it.link}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={it.caption || "Open the post"}
                    >
                      <Media it={it} />
                      {it.media_type === "video" && (
                        <span className="au-gal__play" aria-hidden="true">
                          ▶
                        </span>
                      )}
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="au-gal__btn"
                      aria-label={`Open ${it.caption || "image"}`}
                      onClick={(e) => {
                        opener.current = e.currentTarget;
                        setOpen(zi);
                      }}
                    >
                      <Media it={it} />
                    </button>
                  )}
                  <Caption it={it} />
                </motion.figure>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {open >= 0 && <Lightbox items={zoomable} index={open} onClose={close} onNav={nav} />}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;